import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePatients } from "@/lib/queries";
import { useDeletePatient } from "@/lib/mutations";
import { Has } from "@/components/Has";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash, ChevronLeft, ChevronRight } from "lucide-react";
import { fmtDate } from "@emr/shared/india";
import { computeAgeYm, formatAgeLabel } from "@emr/shared/age";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 20;

export default function Patients() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useMemo(() => q, [q]);
  // Pager (2026-10-31, Batch 4): page state rides the query (usePatients
  // passes page+limit to the API, which returns total). The page resets to 1
  // IN THE SEARCH INPUT HANDLER (not an effect — setState-in-effect triggers
  // cascading renders): debouncedQ === q by identity (useMemo), so every
  // keystroke both re-queries and re-resets the page.
  const [page, setPage] = useState(1);
  const onSearch = (value: string) => {
    setQ(value);
    setPage(1);
  };
  // NOTE (2026-10-31, Batch 4): the raw debounced query is passed — the old
  // `debouncedQ || " "` sentinel (a blank search minted to defeat the hook's
  // `enabled` gate) is gone. Empty search + page 1 → the query is disabled
  // and the page renders its empty state ("Start by creating a patient").
  const { data, isLoading } = usePatients(debouncedQ, page, PAGE_SIZE);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const { isAdmin, permissions } = useAuth();
  const canEdit = isAdmin || permissions.has("patients.edit");
  const canDelete = isAdmin || permissions.has("patients.delete");
  const showActions = canEdit || canDelete;

  // Delete flow (per-row icon). The API is a hard delete; confirm first.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; uhid: string } | null>(null);
  const deleteMut = useDeletePatient();

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch { /* toast handled in mutation */ }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Patients</h1>
        <Has perm="patients.create">
          <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => navigate("/patients/new")}><Plus size={16} className="mr-1" /> New Patient</Button>
        </Has>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input placeholder="Search by name, phone, UHID, old UHID, or ABHA…" className="pl-9" value={q} onChange={(e) => onSearch(e.target.value)} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>UHID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Age/Sex</TableHead>
                <TableHead>Created</TableHead>
                {showActions && <TableHead className="w-24 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={showActions ? 6 : 5}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={showActions ? 6 : 5} className="text-center text-sm text-muted py-8">{q ? "No patients found" : "Start by creating a patient"}</TableCell></TableRow>
              ) : rows.map((p) => (
                <TableRow key={p.id} className="cursor-pointer even:bg-cream-50/50 hover:bg-cream-100 dark:hover:bg-dark-700" onClick={() => navigate(`/patients/${p.id}`)}>
                  <TableCell className="font-mono text-xs">
                  {p.clinicUhid}
                  {p.oldUhid && <div className="text-[10px] text-muted">Old: {p.oldUhid}</div>}
                </TableCell>
                  <TableCell className="font-medium">{[p.salutation, p.name].filter(Boolean).join(" ")}</TableCell>
                  <TableCell>{p.phone ?? "—"}</TableCell>
                  <TableCell>{formatAgeLabel(computeAgeYm(p.dob)) || "—"} / {p.gender?.[0] ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted">{fmtDate(p.createdAt)}</TableCell>
                  {showActions && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <Button
                            size="icon" variant="ghost" className="h-8 w-8"
                            title="Edit patient"
                            onClick={() => navigate(`/patients/${p.id}/edit`)}
                          >
                            <Pencil size={15} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700"
                            title="Delete patient"
                            onClick={() => setDeleteTarget({ id: p.id, name: [p.salutation, p.name].filter(Boolean).join(" "), uhid: p.clinicUhid })}
                          >
                            <Trash size={15} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pager (2026-10-31, Batch 4): Prev/Next off data.total — window.confirm
          simplicity, existing Button component. Rendered whenever a search
          (or paged browse) has results; empty search shows the empty state. */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">
            Page {page} of {totalPages} &middot; {total} patient{total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} className="mr-1" /> Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete patient?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted">
            This will permanently delete <span className="font-medium text-ink dark:text-cream-50">{deleteTarget?.name}</span> ({deleteTarget?.uhid}) and their entire clinical history — visits, notes, prescriptions, lab orders, invoices, and documents. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
